import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { WizardStepService } from '../../../services/wizard-step.service';
import { SupplierRegistryService } from '../../../services/supplier-registry.service';
import { SupplierValidatorsService } from '../../../services/supplier-validators.service';
import { GeographyService, Country, Region, Province, Municipality } from '../../../../core/services/geography.service';
import { FormDropdownService, FormDropdownItem } from '../../../../core/services/form-dropdown.service';
import { MessageService } from 'primeng/api';
import { Supplier } from '../../../models/supplier.model';
import { NoSpecialCharsValidator } from '../../../../core/validators/no-special-chars.validator';
import { ValidationErrorComponent } from '../../../../core/shared/validation-error/validation-error.component';

// PrimeNG 21 Modules
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmationService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { KeyFilterModule } from 'primeng/keyfilter';

@Component({
  selector: 'app-supplier-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    RadioButtonModule,
    InputNumberModule,
    ToastModule,
    ConfirmDialogModule,
    BreadcrumbModule,
    KeyFilterModule,
    ValidationErrorComponent,
  ],
  providers: [MessageService],
  templateUrl: './supplier-form.component.html',
  styleUrls: ['./supplier-form.component.scss'],
})
export class SupplierFormComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  wizardStepService = inject(WizardStepService);
  private supplierService = inject(SupplierRegistryService);
  private geographyService = inject(GeographyService);
  private formDropdownService = inject(FormDropdownService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private confirmDialogService = inject(ConfirmationDialogService);

  // Custom Validators
  static taxCodeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const taxCode = control.value.toString().toUpperCase().replace(/\s/g, '');
    // Codice Fiscale: 16 caratteri alfanumerici (persone fisiche) o 11 cifre (persone giuridiche)
    const taxCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$|^[0-9]{11}$/;
    return taxCodeRegex.test(taxCode) ? null : { invalidTaxCode: true };
  }

  static pecEmailValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    // PEC deve essere un'email valida
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(control.value) ? null : { invalidPecEmail: true };
  }

  static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const phoneNumber = control.value.toString().replace(/\s/g, '');
    // Numero di telefono italiano: +39 seguito da 8-11 cifre, oppure 8-11 cifre senza prefisso
    const phoneRegex = /^(\+39)?[0-9]{8,11}$/;
    return phoneRegex.test(phoneNumber) ? null : { invalidPhoneNumber: true };
  }

  static mobileNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const mobileNumber = control.value.toString().replace(/\s/g, '');
    // Numero di cellulare italiano: +39 seguito da 3 e altre 9 cifre, oppure 3 e altre 9 cifre
    const mobileRegex = /^(\+39)?3[0-9]{9}$/;
    return mobileRegex.test(mobileNumber) ? null : { invalidMobileNumber: true };
  }

  // Signals for 2026 state management
  step1Completed = signal(false);
  supplierId = signal<string | null>(null);
  detailsId = signal<string | null>(null);
  vatCountryOptions = signal<FormDropdownItem[]>([]);
  legacyVatCountry = signal(false);

  get vatNumberHint(): string {
    return SupplierValidatorsService.vatNumberHint(this.step1Form.get('vatCountry')?.value);
  }

  get vatNumberMaxLength(): number {
    return SupplierValidatorsService.vatNumberMaxLength(this.step1Form.get('vatCountry')?.value);
  }

  // WCAG 9.3.2.3: Consistent Navigation - Breadcrumb per navigazione consistente
  breadcrumbItems = [
    { label: 'Registro Fornitori', routerLink: '/axa/suppliers-registry' },
    { label: 'Nuovo Fornitore' }
  ];

  // Cache dei valori originali dei form per ripristino su annullamento
  private originalFormValues = new Map<number, any>();

  ngOnInit(): void {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Nuovo Fornitore - Registro Fornitori - PalEasy');
    
    this.wizardStepService.reset();
    this.initializeWizardSteps();
    
    // Carica i tipi fornitore dall'API
    this.loadSupplierTypes();
    this.loadVatCountries();
    
    // Carica i toponimi per l'indirizzo dall'API
    this.loadPlaceNames();
    
    // Carica la lista dei paesi all'avvio
    this.loadCountries();
    
    // Verifica se c'è un ID nella rotta (modifica bozza)
    this.route.params.subscribe(params => {
      const supplierId = params['id'];
      if (supplierId) {
        this.supplierId.set(supplierId);
        this.loadSupplierData(supplierId);
      }
    });
    
    // Gestione condizionale tipo società e partita IVA
    this.step1Form.get('personType')?.valueChanges.subscribe(type => {
      this.updateSupplierTypeOptions(type);
      this.updateVatNumberValidation(type);
      
      // Enable/disable supplierTypeId based on personType
      if (type) {
        this.step1Form.get('supplierTypeId')?.enable({ emitEvent: false });
      } else {
        this.step1Form.get('supplierTypeId')?.disable({ emitEvent: false });
      }
    });
    
    // Precompila codice fiscale con partita IVA se persona giuridica
    this.step1Form.get('vatCountry')?.valueChanges.subscribe(() => {
      this.step1Form.get('vatNumber')?.updateValueAndValidity({ emitEvent: false });
    });

    this.step1Form.get('vatNumber')?.valueChanges.subscribe(vatNumber => {
      if (this.step1Form.get('personType')?.value === 'LEGAL_ENTITY' && vatNumber) {
        this.step1Form.get('fiscalCode')?.setValue(vatNumber, { emitEvent: false });
      }
    });
    
    // Gestione a cascata per indirizzo geografico
    this.setupAddressListeners();
  }

  private setupAddressListeners(skipInitialCountryCheck: boolean = false): void {
    const addressGroup = this.getAddressGroup();
    if (addressGroup) {
      // Se Italia è già preselezionata, carica le regioni (solo se non stiamo caricando dati da API)
      if (!skipInitialCountryCheck) {
        const initialCountry = addressGroup.get('country')?.value;
        if (initialCountry === 'IT') {
          this.onCountryChange(initialCountry);
        }
      }
      
      addressGroup.get('country')?.valueChanges.subscribe(countryCode => {
        this.onCountryChange(countryCode);
      });
      
      addressGroup.get('region')?.valueChanges.subscribe(regionCode => {
        this.onRegionChange(regionCode);
      });
      
      addressGroup.get('province')?.valueChanges.subscribe(provinceCode => {
        this.onProvinceChange(provinceCode);
      });
    }
  }

  ngOnDestroy(): void {
    this.wizardStepService.reset();
  }

  private initializeWizardSteps(): void {
    this.wizardStepService.steps.set([
      { 
        id: 0, 
        label: 'Anagrafica', 
        completed: false, 
        active: true, 
        icon: 'pi pi-user',
        command: () => this.confirmChangeStep(0)
      },
      { 
        id: 1, 
        label: 'Dettagli', 
        completed: false, 
        active: false, 
        icon: 'pi pi-file-edit',
        command: () => this.confirmChangeStep(1)
      }
    ]);
  }

  /**
   * Verifica se ci sono modifiche non salvate nei form
   */
  private hasUnsavedChanges(): boolean {
    return this.step1Form.dirty || this.step2Form.dirty;
  }

  /**
   * Cambia step con conferma se ci sono modifiche non salvate
   */
  private confirmChangeStep(targetStepId: number): void {
    const currentStepId = this.wizardStepService.currentStep();
    
    // Non fare nulla se clicchiamo sullo step corrente
    if (currentStepId === targetStepId) {
      return;
    }
    
    // Verifica se ci sono modifiche non salvate nel form corrente
    const currentForm = this.getCurrentForm();
    const hasUnsavedChanges = currentForm?.dirty || false;
    
    if (hasUnsavedChanges) {
      // Mostra dialog di conferma con testo standard
      this.confirmDialogService.confirmCancelEdit(() => {
        // Ripristina i valori originali del form corrente
        if (currentForm && this.originalFormValues.has(currentStepId)) {
          const originalValues = this.originalFormValues.get(currentStepId);
          currentForm.reset(originalValues);
        }
        currentForm?.markAsPristine();
        currentForm?.markAsUntouched();
        
        // Procedi con il cambio step
        this.wizardStepService.setStep(targetStepId);
      });
    } else {
      // Nessuna modifica: cambia step direttamente
      this.wizardStepService.setStep(targetStepId);
    }
  }

  /**
   * Ottiene il form corrente in base allo step attivo
   */
  private getCurrentForm(): FormGroup | null {
    return this.getFormByStepId(this.wizardStepService.currentStep());
  }

  /**
   * Ottiene il form associato a uno specifico step ID
   */
  private getFormByStepId(stepId: number): FormGroup | null {
    switch (stepId) {
      case 0:
        return this.step1Form;
      case 1:
        return this.step2Form;
      default:
        return null;
    }
  }

  personTypeOptions = [
    { value: 'INDIVIDUAL', label: 'Persona Fisica' },
    { value: 'LEGAL_ENTITY', label: 'Persona Giuridica' }
  ];

  // Signal per supplier types caricati dall'API
  supplierTypesOptions = signal<FormDropdownItem[]>([]);

  // Signal per toponimi (place names) caricati dall'API
  addressTypologyOptions = signal<FormDropdownItem[]>([]);

  // Signals per dati geografici
  countryOptions = signal<Country[]>([]);
  regionOptions = signal<Region[]>([]);
  provinceOptions = signal<Province[]>([]);
  municipalityOptions = signal<Municipality[]>([]);

  // Signal per memorizzare tutti i tipi fornitore dall'API
  private allSupplierTypes = signal<FormDropdownItem[]>([]);
  
  // Metodo per ottenere i tipi visibili in base a personType
  filteredSupplierTypes(): FormDropdownItem[] {
    const personType = this.step1Form.get('personType')?.value;
    const allTypes = this.allSupplierTypes();
    
    if (!personType) return [];
    
    if (personType === 'INDIVIDUAL') {
      return allTypes.filter(item => item.valueKey === 'DITTA_INDIVIDUALE');
    } else if (personType === 'LEGAL_ENTITY') {
      return allTypes.filter(item => item.valueKey !== 'DITTA_INDIVIDUALE');
    }
    
    return allTypes;
  }

  updateSupplierTypeOptions(personType: string) {
    // Reset tipo società quando cambia person type
    this.step1Form.get('supplierTypeId')?.setValue(null);
  }

  updateVatNumberValidation(personType: string) {
    const vatNumberControl = this.step1Form.get('vatNumber');
    if (personType === 'LEGAL_ENTITY') {
      vatNumberControl?.setValidators([Validators.required, SupplierValidatorsService.vatNumberValidator]);
    } else {
      vatNumberControl?.setValidators([SupplierValidatorsService.vatNumberValidator]);
    }
    vatNumberControl?.updateValueAndValidity();
  }

  // Metodi per caricamento dati da API
  private loadSupplierData(id: string): void {
    this.supplierService.getSupplier(id).subscribe({
      next: (response: any) => {
        const supplier: Supplier = response.data || response;
        
        // Popola il form direttamente con i nomi backend
        this.step1Form.patchValue({
          personType: supplier.personType,
          supplierTypeId: supplier.supplierTypeId,
          supplierName: supplier.supplierName,
          alternativeName: supplier.alternativeName,
          vatCountry: supplier.vatCountry,
          vatNumber: supplier.vatNumber,
          fiscalCode: supplier.fiscalCode,
          primaryEmail: supplier.primaryEmail,
          certifiedEmail: supplier.certifiedEmail,
          telephoneNumber: supplier.telephoneNumber,
          mobileNumber: supplier.mobileNumber,
          contactPerson: supplier.contactPerson,
          axaBuyer: supplier.axaBuyer
        });
        // Suppliers registered before the separate VAT country have no country: it is
        // required again at the next save, with the number entered without prefix.
        this.legacyVatCountry.set(!supplier.vatCountry);
        if (this.legacyVatCountry()) {
          this.step1Form.get('vatCountry')?.markAsTouched();
          this.step1Form.get('vatNumber')?.markAsTouched();
        }
        
        // Popola gli indirizzi
        if (supplier.addresses && supplier.addresses.length > 0) {
          console.log('Loading addresses:', supplier.addresses);
          const addressArray = this.step1Form.get('addresses') as FormArray;
          
          const addr = supplier.addresses[0]; // Usiamo solo il primo indirizzo
          console.log('First address:', addr);
          
          // USA il FormGroup esistente invece di clear() e push()
          const addressGroup = addressArray.at(0) as FormGroup;
          
          // Carica le opzioni geografiche e POI popola i valori
          if (addr.country === 'IT' && addr.region) {
            // Prima carica le regioni
            this.geographyService.getRegions('IT').subscribe({
              next: (regions) => {
                this.regionOptions.set(regions);
                console.log('Regions loaded:', regions.length);
                
                // Poi carica le province
                if (addr.province) {
                  this.geographyService.getProvinces(addr.region!).subscribe({
                    next: (provinces) => {
                      this.provinceOptions.set(provinces);
                      console.log('Provinces loaded:', provinces.length);
                      
                      // Infine carica i comuni
                      if (addr.municipality) {
                        this.geographyService.getMunicipalities(addr.province!).subscribe({
                          next: (municipalities) => {
                            this.municipalityOptions.set(municipalities);
                            console.log('Municipalities loaded:', municipalities.length);
                            
                            // ORA popola tutti i valori dopo aver caricato le opzioni
                            addressGroup.patchValue({
                              id: addr.id ?? null,
                              typology: addr.typology || '',
                              address: addr.address || '',
                              country: addr.country || 'IT',
                              zipCode: addr.zipCode || '',
                              region: addr.region || '',
                              province: addr.province || '',
                              municipality: addr.municipality || '',
                              number: (addr.number ?? null) as string | null
                            }, { emitEvent: false });
                            
                            // Abilita i campi che hanno valori
                            if (addr.province) {
                              addressGroup.get('province')?.enable({ emitEvent: false });
                            }
                            if (addr.municipality) {
                              addressGroup.get('municipality')?.enable({ emitEvent: false });
                            }
                            
                            console.log('Address populated after options loaded:', addressGroup.value);
                          }
                        });
                      } else {
                        // Nessun comune, popola senza municipality
                        addressGroup.patchValue({
                          id: addr.id ?? null,
                          typology: addr.typology || '',
                          address: addr.address || '',
                          country: addr.country || 'IT',
                          zipCode: addr.zipCode || '',
                          region: addr.region || '',
                          province: addr.province || '',
                          municipality: '',
                          number: (addr.number ?? null) as string | null
                        }, { emitEvent: false });
                        
                        // Abilita provincia se ha valore
                        if (addr.province) {
                          addressGroup.get('province')?.enable({ emitEvent: false });
                        }
                        
                        console.log('Address populated without municipality:', addressGroup.value);
                      }
                    }
                  });
                } else {
                  // Nessuna provincia, popola solo con region
                  addressGroup.patchValue({
                    id: addr.id ?? null,
                    typology: addr.typology || '',
                    address: addr.address || '',
                    country: addr.country || 'IT',
                    zipCode: addr.zipCode || '',
                    region: addr.region || '',
                    province: '',
                    municipality: '',
                    number: (addr.number ?? null) as string | null
                  }, { emitEvent: false });
                  console.log('Address populated without province:', addressGroup.value);
                }
              }
            });
          } else {
            // Paese non italiano o senza regione, popola direttamente
            addressGroup.patchValue({
              id: addr.id ?? null,
              typology: addr.typology || '',
              address: addr.address || '',
              country: addr.country || 'IT',
              zipCode: addr.zipCode || '',
              region: addr.region || '',
              province: addr.province || '',
              municipality: addr.municipality || '',
              number: (addr.number ?? null) as string | null
            }, { emitEvent: false });
            console.log('Address populated (non-IT or no region):', addressGroup.value);
          }
        } else {
          console.log('No addresses found in supplier data');
        }
        
        // Carica i dettagli se esistono
        if (supplier.details && supplier.details.id) {
          this.detailsId.set(supplier.details.id.toString());
          this.step2Form.patchValue({
            codeAlboSOL: supplier.details.codeAlboSOL,
            codeSupplierCoupa: supplier.details.codeSupplierCoupa,
            coupaAuthor: supplier.details.coupaAuthor,
            codeSupplierBravoSolution: supplier.details.codeSupplierBravoSolution
          });
        }
        
        // Marca i form come pristine dopo il caricamento dei dati
        // per evitare false segnalazioni di modifiche non salvate
        this.step1Form.markAsPristine();
        this.step2Form.markAsPristine();
        
        // Salva i valori originali per permettere il ripristino su annullamento
        this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step1Form.value)));
        this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step2Form.value)));
        
        this.messageService.add({
          severity: 'info',
          summary: 'Bozza caricata',
          detail: 'I dati della bozza sono stati caricati correttamente'
        });
      },
      error: (err: any) => {
        console.error('Error loading supplier:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i dati del fornitore'
        });
        this.router.navigate(['axa/suppliers-registry']);
      }
    });
  }

  private loadVatCountries(): void {
    this.formDropdownService.getSupplierVatCountries().subscribe({
      next: options => {
        this.vatCountryOptions.set(options);
        this.step1Form.get('vatCountry')?.updateValueAndValidity({ emitEvent: false });
      },
      error: err => {
        console.error('Error loading VAT countries:', err);
        this.step1Form.get('vatCountry')?.setErrors({ vatCountriesUnavailable: true });
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i paesi IVA. Ricarica la pagina prima di salvare.',
        });
      },
    });
  }

  private loadSupplierTypes(): void {
    this.formDropdownService.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.allSupplierTypes.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading supplier types:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i tipi di fornitore'
        });
      }
    });
  }

  private loadPlaceNames(): void {
    this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.addressTypologyOptions.set(response.data);
        }
      },
      error: (err) => {
        console.error('Error loading place names:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i toponimi'
        });
      }
    });
  }

  // Metodi per gestione geografica a cascata
  private loadCountries(): void {
    this.geographyService.getCountries().subscribe({
      next: (countries) => {
        this.countryOptions.set(countries);
      },
      error: (err) => {
        console.error('Error loading countries:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare la lista dei paesi'
        });
      }
    });
  }

  private onCountryChange(countryCode: string | null | undefined): void {
    const addressGroup = this.getAddressGroup();
    if (!addressGroup) return;
    
    // Reset campi dipendenti
    addressGroup.get('region')?.setValue('', { emitEvent: false });
    addressGroup.get('province')?.setValue('', { emitEvent: false });
    addressGroup.get('municipality')?.setValue('', { emitEvent: false });
    this.regionOptions.set([]);
    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);

    const regionControl = addressGroup.get('region');
    if (!regionControl) return;

    // Carica regioni solo se il paese è Italia (code='IT')
    if (countryCode === 'IT') {
      // Italia: usa il dropdown prepopolato, nessun validatore
      regionControl.clearValidators();
      regionControl.updateValueAndValidity({ emitEvent: false });
      
      this.geographyService.getRegions('IT').subscribe({
        next: (regions) => {
          this.regionOptions.set(regions);
        },
        error: (err) => {
          console.error('Error loading regions:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista delle regioni'
          });
        }
      });
    } else {
      // Paese diverso da Italia: campo di testo libero con validatore
      regionControl.setValidators([NoSpecialCharsValidator('address')]);
      regionControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private onRegionChange(regionCode: string | null | undefined): void {
    const addressGroup = this.getAddressGroup();
    if (!addressGroup) return;
    
    // Reset campi dipendenti
    addressGroup.get('province')?.setValue('', { emitEvent: false });
    addressGroup.get('municipality')?.setValue('', { emitEvent: false });
    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);

    // Carica province solo se regione selezionata
    if (regionCode) {
      addressGroup.get('province')?.enable({ emitEvent: false });
      this.geographyService.getProvinces(regionCode).subscribe({
        next: (provinces) => {
          this.provinceOptions.set(provinces);
        },
        error: (err) => {
          console.error('Error loading provinces:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista delle province'
          });
        }
      });
    } else {
      addressGroup.get('province')?.disable({ emitEvent: false });
    }
  }

  private onProvinceChange(provinceCode: string | null | undefined): void {
    const addressGroup = this.getAddressGroup();
    if (!addressGroup) return;
    
    // Reset campi dipendenti
    addressGroup.get('municipality')?.setValue('', { emitEvent: false });
    this.municipalityOptions.set([]);

    // Carica comuni solo se provincia selezionata
    if (provinceCode) {
      addressGroup.get('municipality')?.enable({ emitEvent: false });
      this.geographyService.getMunicipalities(provinceCode).subscribe({
        next: (municipalities) => {
          this.municipalityOptions.set(municipalities);
        },
        error: (err) => {
          console.error('Error loading municipalities:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare la lista dei comuni'
          });
        }
      });
    } else {
      addressGroup.get('municipality')?.disable({ emitEvent: false });
    }
  }

  // Helper per verificare se campo deve essere disabilitato
  isItalySelected(): boolean {
    const addressGroup = this.getAddressGroup();
    return addressGroup?.get('country')?.value === 'IT';
  }

  isRegionDisabled(): boolean {
    const addressGroup = this.getAddressGroup();
    return !addressGroup || addressGroup.get('country')?.value !== 'IT';
  }

  isProvinceDisabled(): boolean {
    const addressGroup = this.getAddressGroup();
    return !addressGroup || !addressGroup.get('region')?.value || this.isRegionDisabled();
  }

  isCityDisabled(): boolean {
    const addressGroup = this.getAddressGroup();
    return !addressGroup || !addressGroup.get('province')?.value || this.isProvinceDisabled();
  }

  // Getter per accedere al FormArray addresses
  get addressArray(): FormArray {
    return this.step1Form.get('addresses') as FormArray;
  }

  // Getter per accedere al primo (e unico) elemento dell'array address
  getAddressGroup(index: number = 0): FormGroup | null {
    const array = this.addressArray;
    if (!array || array.length === 0 || !array.at(index)) {
      return null;
    }
    return array.at(index) as FormGroup;
  }

  step1Form: FormGroup = this.fb.group({
    supplierName: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
    personType: ['', Validators.required],
    supplierTypeId: [null, Validators.required],
    alternativeName: ['', NoSpecialCharsValidator('free-text')],
    vatCountry: ['IT', [
      Validators.required,
      SupplierValidatorsService.vatCountryValidator,
      () => this.vatCountryOptions().length ? null : { vatCountriesUnavailable: true },
    ]],
    vatNumber: ['', SupplierValidatorsService.vatNumberValidator],
    fiscalCode: ['', [Validators.required, SupplierFormComponent.taxCodeValidator, NoSpecialCharsValidator('alphanumeric')]],
    primaryEmail: ['', [Validators.email, NoSpecialCharsValidator('strict')]],
    certifiedEmail: ['', [SupplierFormComponent.pecEmailValidator, NoSpecialCharsValidator('strict')]],
    contactPerson: ['', NoSpecialCharsValidator('name')],
    telephoneNumber: ['', [SupplierFormComponent.phoneNumberValidator, NoSpecialCharsValidator('strict')]],
    mobileNumber: ['', [SupplierFormComponent.mobileNumberValidator, NoSpecialCharsValidator('strict')]],
    axaBuyer: ['', [Validators.required, NoSpecialCharsValidator('name')]],
    addresses: this.fb.array([
      this.fb.group({
        id: [null as number | null],
        typology: [''],
        address: ['', NoSpecialCharsValidator('address')],
        country: ['IT'],
        zipCode: ['', NoSpecialCharsValidator('alphanumeric')],
        region: [''],
        province: ['', NoSpecialCharsValidator('alphanumeric')],
        municipality: ['', (control: AbstractControl) =>
          NoSpecialCharsValidator(
            control.parent?.get('country')?.value === 'IT' ? 'alphanumeric' : 'name'
          )(control)
        ],
        number: [null as string | null]
      })
    ])
  });

  step2Form: FormGroup = this.fb.group({
    codeAlboSOL: [''],
    codeSupplierCoupa: [''],
    coupaAuthor: [''],
    codeSupplierBravoSolution: [''],
  });

  saveDraft() {
    // Metodo che chiama il metodo corretto in base allo step corrente
    if (this.wizardStepService.currentStep() === 0) {
      this.saveDraftStep1();
    } else if (this.wizardStepService.currentStep() === 1) {
      this.saveDraftStep2();
    }
  }

  // Verifica se tutti i campi dell'indirizzo sono vuoti
  private isAddressEmpty(address: any): boolean {
    if (!address) return true;
    
    // Campi dell'indirizzo da verificare (escludiamo 'id' perché è un campo tecnico)
    const addressFields = ['typology', 'address', 'country', 'zipCode', 'region', 'province', 'municipality', 'number'];
    
    // Verifica se tutti i campi sono vuoti, null o la stringa 'IT' per il country (valore di default)
    return addressFields.every(field => {
      const value = address[field];
      // 'IT' è il valore di default per country, quindi lo consideriamo come "non compilato"
      if (field === 'country') {
        return !value || value === 'IT';
      }
      return !value || value === '' || value === null;
    });
  }

  /**
   * Checks if form has special characters errors and returns list of invalid fields
   */
  private hasSpecialCharsErrors(formGroup: FormGroup): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      supplierName: 'Ragione Sociale',
      alternativeName: 'Denominazione commerciale',
      vatNumber: 'Partita IVA',
      fiscalCode: 'Codice Fiscale',
      primaryEmail: 'Email ordinaria',
      certifiedEmail: 'Email PEC',
      contactPerson: 'Persona di Contatto',
      telephoneNumber: 'Telefono',
      mobileNumber: 'Mobile',
      axaBuyer: 'Buyer AXA',
    };

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    // Check address fields in FormArray
    const addressesArray = formGroup.get('addresses') as FormArray;
    if (addressesArray) {
      addressesArray.controls.forEach((addressGroup, index) => {
        const addressFieldLabels: { [key: string]: string } = {
          address: 'Indirizzo',
          zipCode: 'CAP',
          region: 'Regione',
          province: 'Provincia',
          municipality: 'Città',
        };
        
        Object.keys(addressFieldLabels).forEach((fieldName) => {
          const control = addressGroup.get(fieldName);
          if (control?.hasError('specialChars')) {
            fieldsWithErrors.push(addressFieldLabels[fieldName]);
          }
        });
      });
    }

    return fieldsWithErrors;
  }

  saveDraftStep1() {
    if (this.step1Form.get('vatCountry')?.invalid ||
        (this.step1Form.get('vatNumber')?.value && this.step1Form.get('vatNumber')?.invalid)) {
      this.step1Form.get('vatCountry')?.markAsTouched();
      this.step1Form.get('vatNumber')?.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Verifica il paese e il formato della partita IVA prima di salvare.',
      });
      return;
    }
    // Check for special characters errors first
    const specialCharsFields = this.hasSpecialCharsErrors(this.step1Form);
    if (specialCharsFields.length > 0) {
      this.step1Form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggili prima di salvare.`,
        life: 6000,
      });
      return;
    }

    // Valida solo i campi telefono e mobile (se compilati devono essere validi)
    const telephoneControl = this.step1Form.get('telephoneNumber');
    const mobileControl = this.step1Form.get('mobileNumber');
    
    if (telephoneControl?.value && telephoneControl.invalid) {
      telephoneControl.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Il numero di telefono non è valido'
      });
      return;
    }
    
    if (mobileControl?.value && mobileControl.invalid) {
      mobileControl.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Il numero di cellulare non è valido'
      });
      return;
    }
    
    // Usa direttamente i valori del form (già con nomi backend)
    const payload: any = {
      ...this.step1Form.value,
      status: 'DRAFT'
    };
    
    // Rimuovi l'array addresses se tutti i campi sono vuoti
    if (payload.addresses && payload.addresses.length > 0 && this.isAddressEmpty(payload.addresses[0])) {
      delete payload.addresses;
    }
    
    if (!this.supplierId()) {
      // Prima volta: POST
      this.supplierService.createSupplier(payload as Supplier).subscribe({
        next: (response: any) => {
          console.log('Supplier created:', response);
          this.supplierId.set(response.data?.id || response.id);          this.step1Form.markAsPristine();
          this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step1Form.value)));
          this.messageService.add({
            severity: 'success',
            summary: 'Bozza salvata',
            detail: 'La bozza è stata salvata con successo'
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio della bozza'
          });
          console.error('Error saving draft:', err);
        }
      });
    } else {
      // Aggiornamento: PUT
      this.supplierService.updateSupplierPut(this.supplierId()!, payload).subscribe({
        next: (response: any) => {
          // Aggiorna gli ID degli indirizzi se il backend li restituisce
          const updatedSupplier = response.data || response;
          if (updatedSupplier.addresses && updatedSupplier.addresses.length > 0) {
            const addressArray = this.step1Form.get('addresses') as FormArray;
            const addressGroup = addressArray.at(0) as FormGroup;
            const addressId = updatedSupplier.addresses[0].id;
            if (addressId && !addressGroup.get('id')) {
              // Aggiungi il control 'id' se non esiste
              addressGroup.addControl('id', this.fb.control(addressId));
            } else if (addressId) {
              // Aggiorna l'id esistente
              addressGroup.patchValue({ id: addressId }, { emitEvent: false });
            }
          }
                    this.step1Form.markAsPristine();
          this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step1Form.value)));
          this.messageService.add({
            severity: 'success',
            summary: 'Bozza aggiornata',
            detail: 'La bozza è stata aggiornata con successo'
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nell\'aggiornamento della bozza'
          });
          console.error('Error updating draft:', err);
        }
      });
    }
  }

  saveDraftStep2() {
    // Salva bozza step 2 - dettagli fornitore
    if (!this.supplierId()) {
      console.error('Supplier ID is missing. Cannot save step 2.');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fornitore mancante. Torna allo step 1 e salva i dati.'
      });
      return;
    }

    const supplierId = this.supplierId()!;
    const formValues = this.step2Form.value;
    
    // Applica null per valori vuoti
    const detailsPayload = Object.keys(formValues).reduce((acc, key) => {
      acc[key] = formValues[key] || null;
      return acc;
    }, {} as any);
    
    // Combina status e details in un unico payload per la PATCH su /suppliers/5/details
    const combinedPayload = {
      status: 'DRAFT',
      details: detailsPayload
    };
    
    // Una singola chiamata PATCH su /suppliers/{id}/details con status e details insieme
    this.supplierService.updateSupplierDetails(supplierId, combinedPayload).subscribe({
      next: (response: any) => {
        console.log('Details updated:', response);
        // Salva l'id dei dettagli se non già presente
        const detailsId = response.data?.id || response.id;
        if (detailsId && !this.detailsId()) {
          this.detailsId.set(detailsId);
          console.log('Details ID saved:', detailsId);
        }        this.step2Form.markAsPristine();
        this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step2Form.value)));
        this.messageService.add({
          severity: 'success',
          summary: 'Bozza salvata',
          detail: 'La bozza è stata salvata con successo'
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel salvataggio dei dettagli'
        });
        console.error('Error saving details:', err);
      }
    });
  }

  saveStep1AndContinue() {
    // Check for special characters errors first
    const specialCharsFields = this.hasSpecialCharsErrors(this.step1Form);
    if (specialCharsFields.length > 0) {
      this.step1Form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggili prima di proseguire.`,
        life: 6000,
      });
      return;
    }

    // Salva e prosegui con validazione
    if (!this.step1Form.valid) {
      this.step1Form.markAllAsTouched();
      return;
    }

    // Applica null per valori vuoti
    const formValues = this.step1Form.value;
    const payload: any = {
      ...Object.keys(formValues).reduce((acc, key) => {
        acc[key] = formValues[key] || null;
        return acc;
      }, {} as any),
      status: 'DRAFT'
    };
    
    // Rimuovi l'array addresses se tutti i campi sono vuoti
    if (payload.addresses && payload.addresses.length > 0 && this.isAddressEmpty(payload.addresses[0])) {
      delete payload.addresses;
    }
    
    if (!this.supplierId()) {
      // Prima volta: POST
      this.supplierService.createSupplier(payload as Supplier).subscribe({
        next: (response: any) => {
          console.log('Supplier created (step1):', response);
          this.supplierId.set(response.data?.id || response.id);
          this.step1Completed.set(true);
          this.wizardStepService.markStepCompleted(0, true);
          this.wizardStepService.setStep(1);
          this.step1Form.markAsPristine();
          this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step1Form.value)));
          this.messageService.add({
            severity: 'success',
            summary: 'Step 1 completato',
            detail: 'I dati sono stati salvati con successo'
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio dei dati'
          });
          console.error('Error saving step 1:', err);
        }
      });
    } else {
      // Aggiornamento: PUT
      this.supplierService.updateSupplierPut(this.supplierId()!, payload).subscribe({
        next: (response: any) => {
          // Aggiorna gli ID degli indirizzi se il backend li restituisce
          const updatedSupplier = response.data || response;
          if (updatedSupplier.addresses && updatedSupplier.addresses.length > 0) {
            const addressArray = this.step1Form.get('addresses') as FormArray;
            const addressGroup = addressArray.at(0) as FormGroup;
            const addressId = updatedSupplier.addresses[0].id;
            if (addressId && !addressGroup.get('id')) {
              addressGroup.addControl('id', this.fb.control(addressId));
            } else if (addressId) {
              addressGroup.patchValue({ id: addressId }, { emitEvent: false });
            }
          }
          
          this.step1Completed.set(true);
          this.wizardStepService.markStepCompleted(0, true);
          this.wizardStepService.setStep(1);
          this.step1Form.markAsPristine();
          this.originalFormValues.set(0, JSON.parse(JSON.stringify(this.step1Form.value)));
          this.messageService.add({
            severity: 'success',
            summary: 'Step 1 aggiornato',
            detail: 'I dati sono stati aggiornati con successo'
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nell\'aggiornamento dei dati'
          });
          console.error('Error updating step 1:', err);
        }
      });
    }
  }

  goBack() {
    this.confirmChangeStep(0);
  }

  navigateToRegister() {
    // Verifica se ci sono modifiche non salvate
    const currentForm = this.getCurrentForm();
    const hasUnsavedChanges = currentForm?.dirty || false;
    
    if (hasUnsavedChanges) {
      // Mostra dialog di conferma
      this.confirmDialogService.confirmNavigateToRegister('/axa/suppliers-registry');
    } else {
      // Nessuna modifica, naviga direttamente
      this.router.navigate(['/axa/suppliers-registry']);
    }
  }

  saveFinal() {
    // Step 2 non ha campi obbligatori, quindi non serve validazione
    if (!this.supplierId()) {
      console.error('Supplier ID is missing. Cannot complete registration.');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fornitore mancante. Torna allo step 1 e salva i dati.'
      });
      return;
    }

    const supplierId = this.supplierId()!;
    const formValues = this.step2Form.value;
    
    // Applica null per valori vuoti
    const detailsPayload = Object.keys(formValues).reduce((acc, key) => {
      acc[key] = formValues[key] || null;
      return acc;
    }, {} as any);
    
    // Combina status e details in un unico payload per la PATCH su /suppliers/{id}/details
    const combinedPayload = {
      status: 'INACTIVE',
      details: detailsPayload
    };
    
    console.log('Completing registration for supplier ID:', supplierId);
    
    // Una singola chiamata PATCH su /suppliers/{id}/details con status INACTIVE e details insieme
    this.supplierService.updateSupplierDetails(supplierId, combinedPayload).subscribe({
      next: (response: any) => {
        console.log('Details saved:', response);
        // Salva l'id dei dettagli se non già presente
        const detailsId = response.data?.id || response.id;
        if (detailsId && !this.detailsId()) {
          this.detailsId.set(detailsId);
          console.log('Details ID saved:', detailsId);
        }
        this.step2Form.markAsPristine();
        this.originalFormValues.set(1, JSON.parse(JSON.stringify(this.step2Form.value)));
        this.messageService.add({
          severity: 'success',
          summary: 'Registrazione completata',
          detail: 'Il fornitore è stato registrato con successo'
        });
        setTimeout(() => {
          this.router.navigate(['/axa/suppliers-registry']);
        }, 1500);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nella registrazione finale'
        });
        console.error('Error saving final:', err);
      }
    });
  }
}
